import {Ticker} from '../../features/ticker';
import {CartShippingMessage} from '../../features/cart-shipping-message';
import resolution from '../../util/resolution';
import Flickity from 'flickity';

const selectors = {
  announcementBar: '[data-announcement-bar]',
  tickerFrame: '[data-ticker-frame]',
  tickerScale: '[data-ticker-scale]',
  tickerText: '[data-ticker-text]',
};

const classes = {
  isSelected: 'is-selected',
  tickerAnimated: 'ticker--animated',
};

export function announcementTicker(options = {stopClone: false, waitForSlider: false}) {
  return {
    tickerFrames: this.$el.closest(selectors.announcementBar)?.querySelectorAll(selectors.tickerFrame),

    init() {
      if (options?.waitForSlider && this.tickerFrames?.length > 1) {
        // Optionally defer ticker initialization until the announcement slider is loaded
        document.addEventListener('theme:announcement:loaded', this.initTicker.bind(this));
      } else {
        this.initTicker();
      }
    },
    initTicker() {
      new Ticker(this.$el, options?.stopClone);
    },
    toggleTicker(event, isStopped) {
      const tickerScale = this.$el.querySelector(selectors.tickerScale);
      const element = this.$el.querySelector(`[x-ref="${event.detail.blockId}"]`);

      if (!element || element.offsetParent === null) return; // No element or the element is hidden

      if (isStopped) {
        const itemsSpacing = Number(getComputedStyle(element).getPropertyValue('--items-spacing').replace('px', ''));
        const leftPosition = -(element.offsetLeft + itemsSpacing);

        tickerScale.setAttribute('data-stop', '');
        tickerScale.querySelectorAll(selectors.tickerText).forEach((textHolder) => {
          textHolder.classList.remove(classes.tickerAnimated);
          textHolder.style.transform = `translate3d(${leftPosition}px, 0, 0)`;
        });
      }

      if (!isStopped) {
        tickerScale.querySelectorAll(selectors.tickerText).forEach((textHolder) => {
          textHolder.classList.add(classes.tickerAnimated);
          textHolder.removeAttribute('style');
        });
        tickerScale.removeAttribute('data-stop');
      }
    },
    onBlockSelect(event) {
      this.toggleTicker(event, true);
    },
    onBlockDeselect(event) {
      this.toggleTicker(event, false);
    },
  };
}

export function announcementSlider(speed = 4000) {
  return {
    options: {
      initialIndex: 0,
      autoPlay: speed,
      contain: true,
      pageDots: false,
      adaptiveHeight: true,
      wrapAround: true,
      groupCells: false,
      cellAlign: 'left',
      freeScroll: false,
      prevNextButtons: true,
      draggable: true,
      rightToLeft: window.isRTL,
      on: {
        ready: () => {
          setTimeout(() => {
            this.$el.dispatchEvent(
              new CustomEvent('theme:announcement:loaded', {
                bubbles: true,
                detail: {
                  slider: this.$el,
                },
              })
            );
          }, 50);
        },
      },
    },
    flkty: null,
    desktopSlides: [],
    mobileSlides: [],
    isMobileSliderInitialized: false,

    init() {
      this.$nextTick(() => {
        // Slow down the initialization so the 'target referrer' slides get removed by the 'x-target-referrer' directive first
        if (this.$el.children.length < 2) return;

        this.flkty = new Flickity(this.$el, this.options);

        this.removeDeviceSpecificSlides();

        resolution.onChange(() => {
          this.updateDeviceSpecificSlides();
        });

        this.scrollEvent = () => this.scrollEvents();
        this.resizeEvent = () => this.resizeEvents();

        document.addEventListener('theme:resize', this.resizeEvent);
        document.addEventListener('theme:scroll', this.scrollEvent);
      });
    },

    removeDeviceSpecificSlides() {
      const slides = this.flkty.getCellElements();

      slides.forEach((slide, index) => {
        slide.classList.remove(classes.isSelected);

        if (slide.dataset.device == 'desktop') {
          this.desktopSlides.push([slide, index]);

          if (resolution.isMobile()) {
            this.flkty.remove(slide);
          }
        }

        if (slide.dataset.device == 'mobile') {
          this.mobileSlides.push([slide, index]);

          if (resolution.isDesktop() || resolution.isTablet()) {
            this.flkty.remove(slide);
          }
        }
      });

      // Fix slides "transform" position bug that appears after resizing the window just before the slide changes
      setTimeout(() => {
        this.flkty.resize();
      }, 1000);

      this.handleSliderAutoplay();
      this.isMobileSliderInitialized = resolution.isMobile();
    },

    updateDeviceSpecificSlides() {
      if (resolution.isDesktop() || resolution.isTablet()) {
        if (this.isMobileSliderInitialized) {
          // Insert desktop-only slides
          this.desktopSlides.forEach((slide) => {
            this.flkty.insert(slide[0], slide[1]);
          });

          // Remove mobile-only slides
          this.mobileSlides.forEach((slide) => {
            this.flkty.remove(slide[0]);
          });
        }
      }

      if (resolution.isMobile()) {
        if (!this.isMobileSliderInitialized) {
          // Insert mobile-only slides
          this.mobileSlides.forEach((slide) => {
            this.flkty.insert(slide[0], slide[1]);
          });

          // Remove desktop-only slides
          this.desktopSlides.forEach((slide) => {
            this.flkty.remove(slide[0]);
          });
        }
      }

      // Fix slides "transform" position bug that appears after resizing the window just before the slide changes
      setTimeout(() => {
        this.flkty.resize();
      }, 1000);

      this.handleSliderAutoplay();
      this.isMobileSliderInitialized = resolution.isMobile();
    },

    handleSliderAutoplay() {
      if (this.flkty?.slides.length > 1) {
        this.flkty.playPlayer();
      } else {
        this.flkty.pausePlayer();
      }
    },

    resizeEvents() {
      this.flkty.resize();
    },
    scrollEvents() {
      if (!this.flkty.options.autoPlay) return;

      const slider = this.flkty.element;
      const sliderBottomPosition = slider.getBoundingClientRect().top + window.scrollY + slider.offsetHeight;
      if (window.pageYOffset > sliderBottomPosition) {
        if (this.flkty.player.state === 'playing') {
          this.flkty.pausePlayer();
        }
      } else if (this.flkty.player.state === 'paused') {
        this.flkty.playPlayer();
      }
    },
    onBlockSelect(event) {
      const slide = this.$el.querySelector(`[x-ref="${event.detail.blockId}"]`);
      if (!slide) return;
      const slideIndex = parseInt([...slide.parentNode.children].indexOf(slide));

      // Go to selected slide, pause autoplay
      this.flkty.selectCell(slideIndex);
      this.flkty.stopPlayer();
    },
    onBlockDeselect() {
      this.flkty.playPlayer();
    },
    onUnload() {
      this.flkty.options.watchCSS = false;
      this.flkty.destroy();
    },
  };
}

export function announcement() {
  new CartShippingMessage(this.$el);
}
